import _ from "lodash";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import classes from "./PostSelection.scss";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import { EmptySvgIcon } from "SvgComponents";
import PostFeed from "./PostFeed";
import {
  getNodePostQuery,
  getOrganizationPostQuery,
} from "Post/modules/PostQuery";
import { getNodePostFromCache } from "Post/modules/PostGraphqlHelpers";
import { checkScrollAtBottom, prepareLoadMoreFeed } from "Utils";
import { getPostsMemoize } from "./Utils";

const EmptyView = ({ t }) => {
  return (
    <div className={classes.noDataContainer}>
      <EmptySvgIcon />
      <div className={classes.noPostsText}>{t("journal:no_posts")}</div>
    </div>
  );
};

class PostSelection extends PureComponent {
  constructor(props) {
    super(props);
  }

  handleScroll = e => {
    const containerDiv = e.target;
    const { hasNextPage, areMorePostLoading, loadMoreFeed } = this.props;

    if (
      containerDiv &&
      hasNextPage &&
      !areMorePostLoading &&
      checkScrollAtBottom(containerDiv)
    ) {
      loadMoreFeed();
    }
  };

  componentDidMount() {
    const { posts, setPostCount } = this.props;
    setPostCount(_.get(posts, "length", 0));
  }

  componentDidUpdate(prevProps) {
    const { posts, setPostCount } = this.props;
    const prevPostsLength = _.get(prevProps.posts, "length", 0);
    const currentPostsLength = _.get(posts, "length", 0);

    if (prevPostsLength !== currentPostsLength) {
      setPostCount(currentPostsLength);
    }
  }

  render() {
    const {
      posts,
      areMorePostLoading,
      isMultiSelect,
      t,
      selectedItems,
      updateSelectedItems,
      styles,
      disabledItems = [],

      customHeaderElement,
      cardWrapperElement,

      totalCount,

      customEmptyViewElement,
      openViewMode,
      isCompactUINeeded,
      customCardComponent,
      testId,
    } = this.props;
    const postsLength = _.get(posts, "length", 0);

    return (
      <div
        className={classes.container}
        onScroll={this.handleScroll}
        style={
          isCompactUINeeded
            ? styles.updatedContainerStyle
            : styles.containerStyle
        }
      >
        {postsLength > 0 ? (
          <div className={classes.feedContainer}>
            <PostFeed
              items={posts}
              selectedItems={selectedItems}
              updateSelectedItems={updateSelectedItems}
              isMultiSelect={isMultiSelect}
              styles={styles}
              disabledItems={disabledItems}
              customHeaderElement={customHeaderElement}
              totalCount={totalCount}
              cardWrapperElement={cardWrapperElement}
              openViewMode={openViewMode}
              customCardComponent={customCardComponent}
              testId={testId}
            />
            {areMorePostLoading && (
              <div className={classes.loadingContainer}>
                <Loading />
              </div>
            )}
          </div>
        ) : customEmptyViewElement ? (
          customEmptyViewElement
        ) : (
          <EmptyView t={t} />
        )}
      </div>
    );
  }
}

const mapActionCreators = {};

const mapStateToProps = (state, ownProps) => {
  const orgId = _.get(state, "login.userInfo.org_id", "");
  return {
    isData: true,
    isLoading: false,
    orgId,
  };
};

PostSelection.defaultProps = {
  isMultiSelect: false,
  styles: {},
};

export default compose(
  I18nHOC({ resource: ["common", "journal"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getNodePostQuery, {
    name: "getNodePost",
    skip: ({ isProjectNode }) => isProjectNode,
    options: ({ parentId, parentType, filters }) => {
      return {
        /**HACK:To hide previously selected posts,we can not use cache-and-network policy.
         * TODO: find better solution to fix issue
         */
        fetchPolicy: "network-only",
        variables: {
          id: parentId,
          type: parentType,
          filters,
        },
      };
    },
    props({
      getNodePost,
      ownProps: { isData, isLoading, parentId, parentType, filters },
    }) {
      const data = getNodePostFromCache({
        parentId,
        parentType,
        filters,
      });

      const posts = getPostsMemoize({ postsEdges: _.get(data, "posts.edges") });
      const totalCount = _.get(data, "posts.totalCount", 0);

      return {
        posts,
        hasNextPage: _.get(data, "posts.pageInfo.hasNextPage", false),
        areMorePostLoading: getNodePost["networkStatus"] === 3,

        totalCount,
        isData: isData && !_.isEmpty(data),
        isLoading:
          isLoading ||
          getNodePost["networkStatus"] == 1 ||
          getNodePost["networkStatus"] == 2,
        loadMoreFeed: () => {
          return getNodePost.fetchMore({
            variables: {
              id: parentId,
              type: parentType,
              filters,
              after: _.get(getNodePost.node, "posts.pageInfo.endCursor", ""),
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const previousItems = previousResult.node.posts;
              const nextItems = fetchMoreResult.node.posts;

              const previousEdges = previousItems.edges;
              const nextEdges = nextItems.edges;

              const previousPageInfo = previousItems.pageInfo;
              const nextPageInfo = nextItems.pageInfo;

              const nextEndCursor = nextPageInfo.endCursor;
              const nextHasNextPage = nextPageInfo.hasNextPage;

              return {
                ...previousResult,
                node: {
                  ...previousResult.node,
                  posts: {
                    ...previousItems,
                    edges: [...previousEdges, ...nextEdges],
                    pageInfo: {
                      ...previousPageInfo,
                      endCursor: nextEndCursor,
                      hasNextPage: nextHasNextPage,
                    },
                  },
                },
              };
            },
          });
        },
      };
    },
  }),
  graphql(getOrganizationPostQuery, {
    name: "getOrgPost",
    skip: ({ isProjectNode, projectNodeDetails }) =>
      !isProjectNode || _.isEmpty(projectNodeDetails?.projectIds),
    options: ({ projectNodeDetails = {}, filters, orgId }) => {
      return {
        fetchPolicy: "network-only",
        variables: {
          id: orgId,
          filters: {
            parentIds: projectNodeDetails.projectIds,
            parentType: projectNodeDetails.parentType,
            academicYears: filters.academicYears,
            pypElements: filters.pypElements,
            searchText: filters.searchText,
            state: filters.state,
          },
        },
      };
    },
    props({ getOrgPost, ownProps: { isData, isLoading } }) {
      const data = getOrgPost.node;

      const posts = getPostsMemoize({
        postsEdges: _.get(getOrgPost, "node.posts.edges"),
      });
      const totalCount = _.get(getOrgPost, "node.posts.totalCount", 0);

      const { endCursor = "", hasNextPage = false } = _.get(
        data,
        "posts.pageInfo",
        {}
      );

      return {
        posts,
        hasNextPage,
        areMorePostLoading: getOrgPost["networkStatus"] === 3,
        totalCount,
        isData: isData && !!_.get(getOrgPost, "node", null),
        isLoading:
          isLoading ||
          getOrgPost["networkStatus"] == 1 ||
          getOrgPost["networkStatus"] == 2,

        networkStatus: getOrgPost.networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          query: getOrganizationPostQuery,
          variables: {
            ...getOrgPost.variables,
            after: endCursor,
          },
          queryData: getOrgPost,
          customPathString: "node.posts",
        }),
      };
    },
  }),
  withLoader
)(PostSelection);

PostSelection.defaultProps = {
  customEmptyViewElement: null,
  setPostCount: () => {},
};
